import { Fragment, useContext, useEffect, useMemo, useState } from "react";

import { getReferenceString, parseReference } from "@medplum/core";
import { Observation, ServiceRequest } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Stack, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { Loading } from "@/components/Loading";
import ScrollableTable from "@/components/ScrollableTable";
import { ProfileContext } from "@/contexts/profile";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { BloodTypeCodes, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { mergeBloodTypeTests } from "@/health-gorilla/services/manual-labs";
import { HealthGorillaCodes } from "@/health-gorilla/types/labs";
import {
  getLabObservationResultDisplay,
  getLabObservationResultInterpretation,
  getLabOrderResultRangeDisplay,
} from "@/health-gorilla/utils/labs";
import {
  getCodeableConceptCode,
  getCodeableConceptDisplay,
  getResourcesFromBundle,
} from "@/utils/medplum";

import LabOrderResultsTrendDrawer from "./LabOrderResultsTrendDrawer";

interface LabOrderResultsTableProps {
  labOrder: ServiceRequest;
}

export default function LabOrderResultsTable({ labOrder }: LabOrderResultsTableProps) {
  const medplum = useMedplum();
  const labOrderReferenceString = getReferenceString(labOrder);
  const [resultsPerReference, setResultsPerReference] = useState<Record<string, Observation>>({});
  const [labTests, setLabTests] = useState<Observation[]>([]);
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [resultTrendDrawerOpened, { open: openResultTrendDrawer, close: closeResultTrendDrawer }] =
    useDisclosure(false);
  const [selectedResult, setSelectedResult] = useState<Observation | undefined>(undefined);
  const { isPatient } = useContext(ProfileContext);

  useEffect(() => {
    startLoading();
    (async () => {
      try {
        const diagnosticReport = await medplum.searchOne("DiagnosticReport", {
          "based-on": labOrderReferenceString,
          _sort: "-_lastUpdated",
        });
        const resultIds = diagnosticReport?.result
          ?.filter((result) => parseReference(result)[0] === "Observation")
          .map((result) => parseReference(result)[1]);
        if (!diagnosticReport || !resultIds) return;

        const { bundle: resultsBundle } = await medplum.searchResources("Observation", {
          subject: diagnosticReport.subject?.reference,
          _id: resultIds.join(","),
          _count: resultIds.length,
        });
        const labTests = getResourcesFromBundle("Observation", resultsBundle)?.filter(
          (result) => getCodeableConceptCode(result.code) !== HealthGorillaCodes.ClinicalPDFReport,
        );

        const resultsPerReference = (
          await Promise.all(
            labTests.flatMap((labTest) =>
              labTest.hasMember?.map((member) =>
                medplum.searchOne("Observation", {
                  _id: parseReference(member)[1],
                  _sort: "-_lastUpdated",
                }),
              ),
            ),
          )
        ).reduce(
          (acc, result) => {
            if (!result) return acc;
            return { ...acc, [getReferenceString(result)]: result };
          },
          {} as Record<string, Observation>,
        );

        setLabTests(labTests);
        setResultsPerReference(resultsPerReference);
      } finally {
        stopLoading();
      }
    })();
  }, [labOrderReferenceString, medplum, startLoading, stopLoading]);

  const handleOpenResultTrendDrawer = (result: Observation) => {
    setSelectedResult(result);
    openResultTrendDrawer();
  };

  const handleCloseResultTrendDrawer = () => {
    setSelectedResult(undefined);
    closeResultTrendDrawer();
  };

  const mergedLabTests: Observation[] = useMemo(() => {
    return mergeBloodTypeTests(labTests);
  }, [labTests]);

  if (!isLoading && labTests.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs">
      <LabOrderResultsTrendDrawer
        result={selectedResult}
        opened={resultTrendDrawerOpened}
        onClose={handleCloseResultTrendDrawer}
      />
      <ScrollableTable
        verticalSpacing="md"
        horizontalSpacing="lg"
        scrollProps={{ mt: "xs", mb: 0 }}
      >
        <Table.Thead c="cream.6" bg="cream.2">
          <Table.Tr>
            <Table.Th colSpan={3}>Test</Table.Th>
            <Table.Th colSpan={2}>Result</Table.Th>
            <Table.Th colSpan={2}>Units</Table.Th>
            <Table.Th colSpan={2}>Reference Range</Table.Th>
            <Table.Th colSpan={2}>Interpretation</Table.Th>
            <Table.Th ta="right" colSpan={1}>
              Action
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? (
            <Table.Tr>
              <Table.Td colSpan={13}>
                <Loading height={96} />
              </Table.Td>
            </Table.Tr>
          ) : (
            mergedLabTests.map((labTest) => {
              let results = (labTest.hasMember
                ?.map((member) => {
                  const reference = getReferenceString(member);
                  if (!reference) return;
                  return (
                    resultsPerReference[reference] ||
                    labTests.find((t) => getReferenceString(t) === reference)
                  );
                })
                .filter(Boolean) ?? []) as Observation[];

              if (
                (labTest.valueString || labTest.valueQuantity || labTest.valueCodeableConcept) &&
                results.length === 0
              ) {
                results = [labTest];
              }

              const aboResult = results.find(
                (result) => getCodeableConceptCode(result.code) === LOINCCodes.ABOGroup,
              );
              const rhResult = results.find(
                (result) => getCodeableConceptCode(result.code) === LOINCCodes.RhType,
              );

              if (aboResult && rhResult) {
                const aboValue = getLabObservationResultDisplay(aboResult);
                const rhRawValue = getLabObservationResultDisplay(rhResult);
                let rhSymbol = rhRawValue;

                const rhCode = getCodeableConceptCode(rhResult.valueCodeableConcept);
                if (rhCode === LOINCCodes.RhPositive) {
                  rhSymbol = "+";
                } else if (rhCode === LOINCCodes.RhNegative) {
                  rhSymbol = "-";
                }

                const combinedValue = `${aboValue}${rhSymbol}`;

                const combinedObservation: Observation = {
                  ...aboResult,
                  id: `combined-${aboResult.id}-${rhResult.id}`,
                  code: {
                    coding: [
                      {
                        display: "Blood Type",
                        system: QuiltedHealthCodeSystems.BloodType,
                        code: BloodTypeCodes.CombinedBloodType,
                      },
                    ],
                  },
                  valueString: combinedValue,
                  valueCodeableConcept: undefined,
                  valueQuantity: undefined,
                  interpretation: undefined, // Clear interpretation to avoid confusion or merge if needed
                };

                // Replace the individual ABO and Rh results with the combined one
                // Keep other results if any (though usually blood type panel only has these two)
                results = [
                  combinedObservation,
                  ...results.filter(
                    (result) => result.id !== aboResult.id && result.id !== rhResult.id,
                  ),
                ];
              }

              return (
                <Fragment key={labTest.id}>
                  <Table.Tr fw="bold" bg="gray.0">
                    <Table.Td colSpan={12}>{getCodeableConceptDisplay(labTest.code)}</Table.Td>
                  </Table.Tr>
                  {results.map((result) => {
                    const [interpretation, interpretationColor] =
                      getLabObservationResultInterpretation(result, isPatient);
                    return (
                      <Table.Tr key={result.id}>
                        <Table.Td fw="bold" c="gray.6" colSpan={3}>
                          {getCodeableConceptDisplay(result.code)}
                        </Table.Td>
                        <Table.Td colSpan={2}>{getLabObservationResultDisplay(result)}</Table.Td>
                        <Table.Td colSpan={2}>{result.valueQuantity?.unit ?? ""}</Table.Td>
                        <Table.Td colSpan={2}>{getLabOrderResultRangeDisplay(result)}</Table.Td>
                        <Table.Td fw="bold" colSpan={2} c={interpretationColor}>
                          {interpretation ?? ""}
                        </Table.Td>
                        <Table.Td ta="right" colSpan={1}>
                          <Button
                            variant="outline"
                            onClick={() => handleOpenResultTrendDrawer(result)}
                            data-testid={`lab-result-trend-${getCodeableConceptCode(result.code)}`}
                          >
                            Trend
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Fragment>
              );
            })
          )}
        </Table.Tbody>
      </ScrollableTable>
    </Stack>
  );
}
